import React, { useState, useEffect, useCallback } from 'react';
import { MaterialReactTable } from 'material-react-table';
import {
    Button,
    TextField,
    Box,
    Typography,
    Stack,
    Snackbar,
    Alert,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Modal,
    Tooltip
} from '@mui/material';
import { Save, Remove } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
    saveShoppingList,
    getAllDemands,
    getDemand
} from '@store/feature/tradingadvisor/tradingAdvisorSlice';
import Buy from './Buy';

// Add this helper function to convert to Float32 format
const toFloat32 = (value) => {
    // Handle empty/null/undefined values
    if (value === '' || value === null || value === undefined) {
        return '';
    }

    // Convert to number and ensure float32 precision
    const float32 = new Float32Array([Number(value)])[0];

    // Return the float32 value
    return float32;
};

const HedgingTable = ({ shoppingList, productType, contract, isLoading }) => {
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.user);
    const { selectedDemand } = useSelector((state) => state.demand);


    const [data, setData] = useState(selectedDemand?.shopping_list || []);

    const [expandedRows, setExpandedRows] = useState({});
    const [editingRows, setEditingRows] = useState({}); // new state for edit mode
    const [focusedField, setFocusedField] = useState(null); // Track focused field: {rowId, detailIndex, fieldType}
    const [errorMessage, setErrorMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [selectedContract, setSelectedContract] = useState(null);
    const [buyVolume, setBuyVolume] = useState(0);
    const [shoppingItemUUID, setShoppingItemUUID] = useState('');
    const [hedgingItemUUID, setHedgingItemUUID] = useState('');

    const [openBuyModal, setOpenBuyModal] = useState(false);

    const handleOpenBuyModal = useCallback(
        (shoppingUUID, hedgingUUID, selectedRowContract, volume) => {
            setShoppingItemUUID(shoppingUUID);
            setHedgingItemUUID(hedgingUUID);
            setBuyVolume(volume);
            // Use contract (prop) to get contract details instead of theeur.contracts
            const contractKey = selectedRowContract;
            if (contract && Object.keys(contract).includes(contractKey)) {
                const selectedContractData = {
                    key: contractKey,
                    ...contract[contractKey]
                };
                setSelectedContract(selectedContractData);
                setOpenBuyModal(true);
            } else {
                alert('Selected contract not found. Please try again.');
            }
        },
        [contract]
    );
    const handleCloseBuyModal = () => setOpenBuyModal(false);

    // const strategies = ['Max Buy', 'Min Buy', 'Trailing Stop'];

    // New useEffect to update local data when shoppingList prop changes
    useEffect(() => {
        setData(shoppingList);
    }, [shoppingList]);

    // Update data when selectedDemand changes
    // useEffect(() => {
    //     if (selectedDemand?.shopping_list) {
    //       setData(selectedDemand.shopping_list);
    //     }
    //   }, [selectedDemand]);

    const handleDetailChange = (rowId, detailIndex, field, value) => {
        setExpandedRows((prev) => {
            const newDetails = [...(prev[rowId] || [])];
            newDetails[detailIndex] = {
                ...newDetails[detailIndex],
                [field]: value
            };
            return { ...prev, [rowId]: newDetails };
        });
    };

    const handleAddDetail = (rowId) => {
        setExpandedRows((prev) => ({
            ...prev,
            [rowId]: [...(prev[rowId] || []), { strategy: { name: 'none' } }]
        }));
    };

    const handleRemoveDetail = (rowId, detailIndex) => {
        // Get the detail
        const detail = expandedRows[rowId]?.[detailIndex];

        // Check if it has been bought
        if (detail && detail.volume_bought > 0) {
            setErrorMessage('Cannot remove an item that has been purchased');
            return;
        }

        // Proceed with removal if not bought
        setExpandedRows((prev) => {
            const newDetails = [...prev[rowId]];
            newDetails.splice(detailIndex, 1);
            return { ...prev, [rowId]: newDetails };
        });
    };

    const validateVolumes = (rowId, details) => {
        const row = data.find((r) => r.uuid === rowId);
        const totalInputVolume = details.reduce(
            (sum, detail) => sum + (parseFloat(detail.volume) || 0),
            0
        );

        if (totalInputVolume > row.volume) {
            setErrorMessage(
                `Total volume (${totalInputVolume}) exceeds available volume (${row.volume})`
            );
            return false;
        }
        return true;
    };

    // Validate hedging dates for each detail
    const hedgingDateValidation = (rowId, details) => {
        const invalidDates = details.some(
            (detail) =>
                new Date(detail.hedgingStart) > new Date(detail.hedgingEnd)
        );

        if (invalidDates) {
            setErrorMessage('Invalid Hedging start date');
            return false;
        }
        return true;
    };

    // Validate hedging start against hedging_start_before constraint
    const hedgingStartBeforeValidation = (rowId, details) => {
        const row = data.find((r) => r.uuid === rowId);

        for (let i = 0; i < details.length; i++) {
            const detail = details[i];
            const originalHedgingData = row?.hedging_data?.[i];

            if (
                originalHedgingData?.hedging_start_before &&
                detail.hedgingStart
            ) {
                const hedgingStart = new Date(detail.hedgingStart);
                const hedgingStartBefore = new Date(
                    originalHedgingData.hedging_start_before
                );

                if (hedgingStart > hedgingStartBefore) {
                    setErrorMessage(
                        `Hedging can only be started before ${hedgingStartBefore.toLocaleDateString()}. Please select an earlier date.`
                    );
                    return false;
                }
            }
        }
        return true;
    };

    const handleEditRow = (row) => {
        // Get current market price for the contract
        const currentPrice =
            contract &&
            contract[row.original.contract] &&
            contract[row.original.contract].ask
                ? Number(contract[row.original.contract].ask)
                : 0;

        // Pre-populate the expandedRows with existing hedging_data from row
        setExpandedRows((prev) => ({
            ...prev,
            [row.original.uuid]: row.original.hedging_data.map((detail) => {
                // If strategy is trailing stop and doesn't have price, add current market price
                let strategy = detail.strategy;

                // If no strategy exists, default to 'none'
                if (!strategy || !strategy.name) {
                    strategy = { name: 'none' };
                } else if (
                    strategy.name === 'trailingstop' &&
                    strategy.trailing_stop_strategy &&
                    !strategy.trailing_stop_strategy.price
                ) {
                    strategy = {
                        ...strategy,
                        trailing_stop_strategy: {
                            ...strategy.trailing_stop_strategy,
                            price: currentPrice
                        }
                    };
                }

                return {
                    volume: detail.volume,
                    hedgingStart: detail.hedging_start_str,
                    hedgingEnd: detail.hedging_end_str,
                    strategy: strategy
                };
            })
        }));
        setEditingRows((prev) => ({ ...prev, [row.original.uuid]: true }));
    };

    const handleSaveRow = async (rowId) => {
        try {
            const details = expandedRows[rowId] || [];

            const row = data.find((r) => r.uuid === rowId);

            // Validate required fields
            const invalidDetails = details.some(
                (detail) =>
                    !detail.volume ||
                    !detail.hedgingStart ||
                    !detail.hedgingEnd ||
                    !detail.strategy ||
                    !detail.strategy.name
            );
            if (invalidDetails) {
                setErrorMessage('Please fill in all required fields');
                return;
            }

            // Validate volumes
            if (!validateVolumes(rowId, details)) {
                return;
            }

            // Validate hedging dates
            if (!hedgingDateValidation(rowId, details)) {
                return;
            }

            // Validate hedging start against hedging_start_before constraint
            if (!hedgingStartBeforeValidation(rowId, details)) {
                return;
            }

            // Get the existing hedging_data from shoppingList (data) for this row and return the prepared hedging data
            const originalHedgingData =
                data.find((item) => item.uuid === rowId)?.hedging_data || [];

            const hedgingData = details.map((detail, index) => {
                // Try to retrieve uuid from expandedRows first
                const existingUUID = expandedRows[rowId]?.[index]?.uuid || '';
                // If not there, check original data's hedging_data
                const originalUUID = originalHedgingData[index]?.uuid || '';

                // Process strategy and convert margins to Float32 only when saving
                let processedStrategy = detail.strategy;
                if (
                    detail.strategy?.name === 'trailingstop' &&
                    detail.strategy.trailing_stop_strategy
                ) {
                    processedStrategy = {
                        ...detail.strategy,
                        trailing_stop_strategy: {
                            ceiling_margin: detail.strategy
                                .trailing_stop_strategy.ceiling_margin
                                ? toFloat32(
                                      detail.strategy.trailing_stop_strategy
                                          .ceiling_margin
                                  )
                                : '',
                            floor_margin: detail.strategy.trailing_stop_strategy
                                .floor_margin
                                ? toFloat32(
                                      detail.strategy.trailing_stop_strategy
                                          .floor_margin
                                  )
                                : ''
                        }
                    };
                }

                return {
                    sequence: index + 1,
                    volume: parseFloat(detail.volume),
                    hedging_start_str: detail.hedgingStart,
                    hedging_end_str: detail.hedgingEnd,
                    strategy: processedStrategy,
                    // Use the uuid from original data if available; otherwise, any found in expandedRows
                    uuid: originalUUID || existingUUID,
                    // Set flag to false when the uuid exists, else true
                    flag: originalUUID ? false : existingUUID ? false : true
                };
            });

            // Prepare the data to be sent in the request body
            const shoppingListPayload = {
                uuid: rowId,
                demand_uuid: row.demand_uuid, // Assuming row contains `demand_uuid`
                hedging_data: hedgingData
            };

            // Send the POST request
            const response = await dispatch(
                saveShoppingList(shoppingListPayload)
            );

            if (response?.meta?.requestStatus === 'fulfilled') {
                dispatch(getDemand(shoppingListPayload.demand_uuid));
                dispatch(getAllDemands());
                // Turn off edit mode on save success
                setEditingRows((prev) => ({ ...prev, [rowId]: false }));
            }

            setSnackbarOpen(true);
            setErrorMessage('');
        } catch (error) {
            setErrorMessage(error.message || 'Error saving details');
        }
    };

    // New handler for canceling row edit and resetting expandedRows
    const handleCancelRowEdit = (rowId) => {
        const row = data.find((r) => r.uuid === rowId);
        if (row) {
            setExpandedRows((prev) => ({
                ...prev,
                [rowId]: row.hedging_data.map((detail) => ({
                    volume: detail.volume,
                    hedgingStart: detail.hedging_start_str,
                    hedgingEnd: detail.hedging_end_str,
                    strategy: detail.strategy
                }))
            }));
        }
        setEditingRows((prev) => ({ ...prev, [rowId]: false }));
    };

    // New handler to ensure editing mode and then add a detail
    const handleEnsureEditingAndAddDetail = (row) => {
        if (!editingRows[row.original.uuid]) {
            handleEditRow(row);
            setTimeout(() => {
                handleAddDetail(row.original.uuid);
            }, 0);
        } else {
            handleAddDetail(row.original.uuid);
        }
    };

    // Helper to check if hedging start has validation error
    const hasHedgingStartError = (rowId, detailIndex) => {
        const details = expandedRows[rowId] || [];
        const row = data.find((r) => r.uuid === rowId);
        const detail = details[detailIndex];
        const originalHedgingData = row?.hedging_data?.[detailIndex];

        if (originalHedgingData?.hedging_start_before && detail?.hedgingStart) {
            const hedgingStart = new Date(detail.hedgingStart);
            const hedgingStartBefore = new Date(
                originalHedgingData.hedging_start_before
            );
            return hedgingStart > hedgingStartBefore;
        }
        return false;
    };

    // Helper to get max allowed date for hedging start
    const getMaxHedgingStartDate = (rowId, detailIndex) => {
        const row = data.find((r) => r.uuid === rowId);
        const originalHedgingData = row?.hedging_data?.[detailIndex];

        if (originalHedgingData?.hedging_start_before) {
            // Convert to YYYY-MM-DD format for date input
            return new Date(originalHedgingData.hedging_start_before)
                .toISOString()
                .split('T')[0];
        }
        return undefined;
    };

    // Helper to get validation messages for a row
    const getValidationMessage = (rowId) => {
        const details = expandedRows[rowId] || [];
        const row = data.find((r) => r.uuid === rowId);

        for (let index = 0; index < details.length; index++) {
            const detail = details[index];
            const originalHedgingData = row?.hedging_data?.[index];

            // Check hedging start against hedging_start_before constraint
            if (
                originalHedgingData?.hedging_start_before &&
                detail.hedgingStart
            ) {
                const hedgingStart = new Date(detail.hedgingStart);
                const hedgingStartBefore = new Date(
                    originalHedgingData.hedging_start_before
                );

                if (hedgingStart > hedgingStartBefore) {
                    return `Hedging can only be started before ${hedgingStartBefore.toLocaleDateString()}. Please select an earlier date.`;
                }
            }

            // Check trailing stop margins
            if (detail.strategy && detail.strategy.name === 'trailingstop') {
                const ceilingMargin =
                    detail.strategy.trailing_stop_strategy?.ceiling_margin;
                const floorMargin =
                    detail.strategy.trailing_stop_strategy?.floor_margin;

                if (!ceilingMargin || ceilingMargin === '') {
                    return 'Please enter ceiling margin for trailing stop strategy.';
                }
                if (!floorMargin || floorMargin === '') {
                    return 'Please enter floor margin for trailing stop strategy.';
                }
            }
        }
        return '';
    };

    // Helper to check if save button should be disabled
    const isSaveDisabled = (rowId) => {
        const details = expandedRows[rowId] || [];
        const row = data.find((r) => r.uuid === rowId);

        // Check if hedgingStartBeforeValidation passes
        if (!hedgingStartBeforeValidation(rowId, details)) {
            return true; // Disable save if validation fails
        }

        return details.some((detail, index) => {
            // If strategy is trailing stop, check if ceiling and floor margins have values
            if (detail.strategy && detail.strategy.name === 'trailingstop') {
                const ceilingMargin =
                    detail.strategy.trailing_stop_strategy?.ceiling_margin;
                const floorMargin =
                    detail.strategy.trailing_stop_strategy?.floor_margin;

                // Disable save if either margin is empty or undefined
                if (
                    !ceilingMargin ||
                    !floorMargin ||
                    ceilingMargin === '' ||
                    floorMargin === ''
                ) {
                    return true;
                }
            }

            // For 'none' strategy or any other strategy, don't disable save
            return false;
        });
    };

    // New: helper to update strategy field with an object
    const handleStrategyChange = (rowId, detailIndex, value) => {
        setExpandedRows((prev) => {
            const newDetails = [...(prev[rowId] || [])];

            // Get current market price for the contract
            const row = data.find((r) => r.uuid === rowId);
            const currentPrice =
                contract &&
                contract[row?.contract] &&
                contract[row.contract].ask
                    ? Number(contract[row.contract].ask)
                    : 0;

            newDetails[detailIndex] = {
                ...newDetails[detailIndex],
                strategy:
                    value === 'trailingstop'
                        ? {
                              name: 'trailingstop',
                              trailing_stop_strategy: {
                                  ceiling_margin: '',
                                  floor_margin: '',
                                  price: currentPrice
                              }
                          }
                        : value === 'none'
                        ? { name: 'none' }
                        : { name: value }
            };
            return { ...prev, [rowId]: newDetails };
        });
    };

    // New: helper to update trailing stop margins inside strategy
    const handleTrailingMarginChange = (
        rowId,
        detailIndex,
        marginType,
        value
    ) => {
        // Store the raw string value during editing - don't convert to Float32 yet
        setExpandedRows((prev) => {
            const newDetails = [...(prev[rowId] || [])];

            // Safe check for undefined strategy
            if (!newDetails[detailIndex]?.strategy) {
                newDetails[detailIndex] = {
                    ...newDetails[detailIndex],
                    strategy: {
                        name: 'trailingstop',
                        trailing_stop_strategy: {}
                    }
                };
            }

            // Safe check for trailing_stop_strategy
            if (!newDetails[detailIndex].strategy.trailing_stop_strategy) {
                newDetails[detailIndex].strategy.trailing_stop_strategy = {};
            }

            const currentStrategy = newDetails[detailIndex].strategy;

            if (currentStrategy && currentStrategy.name === 'trailingstop') {
                const updatedStrategy = {
                    ...currentStrategy,
                    trailing_stop_strategy: {
                        ...currentStrategy.trailing_stop_strategy,
                        [marginType]: value // Store the raw string value
                    }
                };

                newDetails[detailIndex].strategy = updatedStrategy;
            }

            return { ...prev, [rowId]: newDetails };
        });
    };

    const columns = [
        {
            accessorKey: 'contract',
            header: 'Contract',
            Cell: ({ row }) => (
                <Typography
                    sx={{
                        color: '#27A9FF',
                        fontWeight: 'bold',
                        fontSize: '14px'
                    }}
                >
                    {row.original.contract}
                </Typography>
            )
        },
        {
            accessorKey: 'product',
            header: 'Product',
            Cell: ({ row }) => (
                <Typography
                    sx={{
                        color: 'black',

                        fontSize: '14px'
                    }}
                >
                    {row.original.product}
                </Typography>
            )
        },
        {
            accessorKey: 'volume',
            header: 'Volume (MWh/h)',
            Cell: ({ row }) => (
                <Typography
                    sx={{ color: 'teal', fontWeight: 'bold', fontSize: '14px' }}
                >
                    {Number(row.original.volume).toFixed(2)}
                </Typography>
            )
        },
        {
            accessorKey: 'price',
            header: 'Price in EUR',
            Cell: ({ row }) => (
                <Typography
                    sx={{
                        color: '#27A9FF',
                        fontWeight: 'bold',
                        fontSize: '14px'
                    }}
                >
                    {contract &&
                    contract[row.original.contract] &&
                    contract[row.original.contract].ask != null
                        ? Number(contract[row.original.contract].ask).toFixed(2)
                        : 'No price available'}
                </Typography>
            )
        },
        {
            accessorKey: 'signal',
            header: 'Current-Signal',
            Cell: ({ row }) => (
                <Typography
                    sx={{
                        color: '#27A9FF',
                        fontWeight: 'bold',
                        fontSize: '14px'
                    }}
                >
                    {row.original.signal}
                    {/* Buy, Sell, Hold */}
                </Typography>
            )
        }
    ];

    return (
        <Box className="w-full">
            <Modal open={openBuyModal} onClose={handleCloseBuyModal}>
                <Buy
                    handleClose={handleCloseBuyModal}
                    open={openBuyModal}
                    selectedContract={selectedContract}
                    user={user}
                    productTypeMarket={productType}
                    buyVolume={buyVolume}
                    shoppingItemUUID={shoppingItemUUID}
                    hedgingItemUUID={hedgingItemUUID}
                />
            </Modal>
            <MaterialReactTable
                columns={columns}
                data={data}
                enableExpanding
                enableSorting={false}
                enableColumnActions={false}
                initialState={{
                    pagination: {
                        pageSize: 50, // Set initial page size to 50
                        pageIndex: 0 // Start at first page
                    },
                    // expanded: true,
                    density: 'compact'
                }}
                state={{ isLoading: isLoading }}
                renderTopToolbarCustomActions={() => (
                    <Typography
                        variant="h6"
                        color="#245372"
                        sx={{ fontWeight: 'bold', ml: 2 }}
                    >
                        Shopping List
                    </Typography>
                )}
                renderDetailPanel={({ row }) => (
                    <Box className="px-4 py-1">
                        {row.original.hedging_data.length > 0 &&
                        !editingRows[row.original.uuid] ? (
                            <>
                                <Stack spacing={0.5}>
                                    <div className="border border-gray-200 rounded overflow-hidden">
                                        {/* Single header row for all items */}
                                        <div className="grid grid-cols-10 bg-gray-50 border-b border-gray-200">
                                            <div className="p-2 font-bold text-xs text-gray-700 border-r border-gray-200">
                                                Volume
                                            </div>
                                            <div className="p-2 font-bold text-xs text-gray-700 border-r border-gray-200">
                                                Strategy
                                            </div>
                                            <div className="p-2 font-bold text-xs text-gray-700 border-r border-gray-200">
                                                Hedging Start
                                            </div>
                                            <div className="p-2 font-bold text-xs text-gray-700 border-r border-gray-200">
                                                Hedging End
                                            </div>
                                            <div className="p-2 font-bold text-xs text-gray-700 border-r border-gray-200">
                                                Bought Volume
                                            </div>
                                            <div className="p-2 font-bold text-xs text-gray-700 border-r border-gray-200">
                                                Remaining Volume
                                            </div>
                                            <div className="p-2 font-bold text-xs text-gray-700 border-r border-gray-200">
                                                Ceiling Price
                                            </div>
                                            <div className="p-2 font-bold text-xs text-gray-700 border-r border-gray-200">
                                                Floor Price
                                            </div>
                                            <div className="p-2 font-bold text-xs text-gray-700">
                                                Action
                                            </div>
                                        </div>

                                        {/* Data rows */}
                                        {row.original.hedging_data.map(
                                            (detail, index) => (
                                                <div
                                                    key={index}
                                                    className="grid grid-cols-10 border-b border-gray-200"
                                                    style={{
                                                        backgroundColor:
                                                            index % 2 === 0
                                                                ? '#ffffff'
                                                                : '#f9f9f9'
                                                    }}
                                                >
                                                    <div className="p-2 text-sm border-r border-gray-200">
                                                        <span className="text-blue-500 font-medium">
                                                            {Number(
                                                                detail.volume
                                                            ).toFixed(2)}
                                                        </span>
                                                    </div>

                                                    <div className="p-2 text-sm border-r border-gray-200">
                                                        {detail.strategy &&
                                                        typeof detail.strategy ===
                                                            'object'
                                                            ? detail.strategy
                                                                  .name ||
                                                              'none'
                                                            : detail.strategy ||
                                                              'none'}
                                                    </div>

                                                    <div className="p-2 text-sm border-r border-gray-200">
                                                        {
                                                            detail.hedging_start_str
                                                        }
                                                    </div>

                                                    <div className="p-2 text-sm border-r border-gray-200">
                                                        {detail.hedging_end_str}
                                                    </div>

                                                    <div className="p-2 text-sm border-r border-gray-200">
                                                        {Number(
                                                            detail.volume_bought
                                                        ).toFixed(2)}
                                                    </div>

                                                    <div className="p-2 text-sm border-r border-gray-200">
                                                        <span
                                                            className={
                                                                detail.volume -
                                                                    detail.volume_bought >
                                                                0
                                                                    ? 'text-blue-500'
                                                                    : 'text-gray-500'
                                                            }
                                                        >
                                                            {(
                                                                Number(
                                                                    detail.volume
                                                                ) -
                                                                Number(
                                                                    detail.volume_bought
                                                                )
                                                            ).toFixed(2)}
                                                        </span>
                                                    </div>

                                                    <div className="p-2 text-sm border-r border-gray-200">
                                                        {detail.strategy
                                                            ?.name ===
                                                        'trailingstop'
                                                            ? detail.strategy
                                                                  ?.trailing_stop_strategy
                                                                  ?.ceiling_margin
                                                                ? Number(
                                                                      detail
                                                                          .strategy
                                                                          .trailing_stop_strategy
                                                                          .ceiling_margin
                                                                  ).toFixed(2)
                                                                : ''
                                                            : ''}
                                                    </div>

                                                    <div className="p-2 text-sm border-r border-gray-200">
                                                        {detail.strategy
                                                            ?.name ===
                                                        'trailingstop'
                                                            ? detail.strategy
                                                                  ?.trailing_stop_strategy
                                                                  ?.floor_margin
                                                                ? Number(
                                                                      detail
                                                                          .strategy
                                                                          .trailing_stop_strategy
                                                                          .floor_margin
                                                                  ).toFixed(2)
                                                                : ''
                                                            : ''}
                                                    </div>

                                                    <div className="p-2 text-sm flex justify-center items-center">
                                                        {Object.keys(
                                                            contract || {}
                                                        ).length > 0 && (
                                                            <Button
                                                                color="primary"
                                                                size="small"
                                                                sx={{
                                                                    fontSize:
                                                                        '12px',
                                                                    fontWeight:
                                                                        'bold',
                                                                    border: '1px solid',
                                                                    padding:
                                                                        '2px 6px',
                                                                    minWidth:
                                                                        'unset'
                                                                }}
                                                                onClick={() => {
                                                                    handleOpenBuyModal(
                                                                        row
                                                                            .original
                                                                            .uuid,
                                                                        detail.uuid,
                                                                        row
                                                                            .original
                                                                            .contract,
                                                                        detail.volume ||
                                                                            0
                                                                    );
                                                                }}
                                                                disabled={
                                                                    detail.volume_bought >=
                                                                        detail.volume &&
                                                                    detail.volume -
                                                                        detail.volume_bought <=
                                                                        0
                                                                }
                                                            >
                                                                Buy
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </Stack>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        mt: 1
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => handleEditRow(row)}
                                        sx={{
                                            fontSize: '13px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Edit
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={() =>
                                            handleEnsureEditingAndAddDetail(row)
                                        }
                                        sx={{
                                            fontSize: '13px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Add Detail
                                    </Button>
                                </Box>
                            </>
                        ) : (
                            // Editable view (either creating new or editing existing details)
                            <Stack spacing={2}>
                                {(() => {
                                    // Calculate this once outside the mapping function
                                    const originalRow = data.find(
                                        (item) =>
                                            item.uuid === row.original.uuid
                                    );

                                    // Now map through details with the pre-computed original row
                                    return expandedRows[row.original.uuid]?.map(
                                        (detail, index) => {
                                            const originalDetail =
                                                originalRow?.hedging_data?.[
                                                    index
                                                ];
                                            const hasPurchases =
                                                originalDetail?.volume_bought >
                                                0;
                                            const canRemove = !hasPurchases;

                                            return (
                                                <Box
                                                    key={index}
                                                    className="flex items-center gap-2 pb-1 border-b"
                                                >
                                                    <Tooltip
                                                        title={
                                                            hasPurchases
                                                                ? 'Volume cannot be changed after purchases'
                                                                : ''
                                                        }
                                                        arrow
                                                    >
                                                        <span>
                                                            <TextField
                                                                label="Volume"
                                                                type="number"
                                                                value={
                                                                    detail.volume ||
                                                                    ''
                                                                }
                                                                onChange={(e) =>
                                                                    handleDetailChange(
                                                                        row
                                                                            .original
                                                                            .uuid,
                                                                        index,
                                                                        'volume',
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                disabled={
                                                                    hasPurchases
                                                                }
                                                                size="small"
                                                                InputLabelProps={{
                                                                    sx: {
                                                                        fontSize:
                                                                            '14px',
                                                                        fontWeight:
                                                                            'bold',
                                                                        color: 'gray'
                                                                    }
                                                                }}
                                                                InputProps={{
                                                                    sx: {
                                                                        fontSize:
                                                                            '14px',
                                                                        fontWeight:
                                                                            'bold',
                                                                        color: hasPurchases
                                                                            ? 'rgba(0, 0, 0, 0.38)'
                                                                            : 'gray'
                                                                    }
                                                                }}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                    <TextField
                                                        label="Hedging Start"
                                                        type="date"
                                                        value={
                                                            detail.hedgingStart ||
                                                            ''
                                                        }
                                                        inputProps={{
                                                            max: getMaxHedgingStartDate(
                                                                row.original
                                                                    .uuid,
                                                                index
                                                            )
                                                        }}
                                                        onChange={(e) =>
                                                            handleDetailChange(
                                                                row.original
                                                                    .uuid,
                                                                index,
                                                                'hedgingStart',
                                                                e.target.value
                                                            )
                                                        }
                                                        size="small"
                                                        error={hasHedgingStartError(
                                                            row.original.uuid,
                                                            index
                                                        )}
                                                        helperText={
                                                            hasHedgingStartError(
                                                                row.original
                                                                    .uuid,
                                                                index
                                                            )
                                                                ? (() => {
                                                                      const originalRow =
                                                                          data.find(
                                                                              (
                                                                                  r
                                                                              ) =>
                                                                                  r.uuid ===
                                                                                  row
                                                                                      .original
                                                                                      .uuid
                                                                          );
                                                                      const originalHedgingData =
                                                                          originalRow
                                                                              ?.hedging_data?.[
                                                                              index
                                                                          ];
                                                                      return originalHedgingData?.hedging_start_before
                                                                          ? `Must be before ${new Date(
                                                                                originalHedgingData.hedging_start_before
                                                                            ).toLocaleDateString()}`
                                                                          : 'Invalid date';
                                                                  })()
                                                                : ''
                                                        }
                                                        InputLabelProps={{
                                                            shrink: true
                                                        }}
                                                        InputProps={{
                                                            sx: {
                                                                fontSize:
                                                                    '14px',
                                                                fontWeight:
                                                                    'bold',
                                                                color: hasHedgingStartError(
                                                                    row.original
                                                                        .uuid,
                                                                    index
                                                                )
                                                                    ? '#d32f2f'
                                                                    : 'gray'
                                                            }
                                                        }}
                                                    />
                                                    <TextField
                                                        label="Hedging End"
                                                        type="date"
                                                        value={
                                                            detail.hedgingEnd ||
                                                            ''
                                                        }
                                                        onChange={(e) =>
                                                            handleDetailChange(
                                                                row.original
                                                                    .uuid,
                                                                index,
                                                                'hedgingEnd',
                                                                e.target.value
                                                            )
                                                        }
                                                        size="small"
                                                        InputLabelProps={{
                                                            shrink: true
                                                        }}
                                                        InputProps={{
                                                            sx: {
                                                                fontSize:
                                                                    '14px',
                                                                fontWeight:
                                                                    'bold',
                                                                color: 'gray'
                                                            }
                                                        }}
                                                    />
                                                    <FormControl
                                                        size="small"
                                                        className="min-w-[200px] w-[10%]"
                                                    >
                                                        <InputLabel
                                                            sx={{
                                                                fontSize:
                                                                    '14px',
                                                                fontWeight:
                                                                    'bold',
                                                                color: 'gray'
                                                            }}
                                                        >
                                                            Strategy
                                                        </InputLabel>

                                                        <Select
                                                            value={
                                                                (detail.strategy &&
                                                                    detail
                                                                        .strategy
                                                                        .name) ||
                                                                'none'
                                                            }
                                                            label="Strategy"
                                                            onChange={(e) =>
                                                                handleStrategyChange(
                                                                    row.original
                                                                        .uuid,
                                                                    index,
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            sx={{
                                                                fontSize:
                                                                    '14px',
                                                                fontWeight:
                                                                    'bold',
                                                                color: 'gray'
                                                            }}
                                                        >
                                                            {[
                                                                'none',
                                                                'trailingstop'
                                                                // 'minbuy',
                                                                // 'maxbuy'
                                                            ].map(
                                                                (strategy) => (
                                                                    <MenuItem
                                                                        key={
                                                                            strategy
                                                                        }
                                                                        value={
                                                                            strategy
                                                                        }
                                                                    >
                                                                        {
                                                                            strategy
                                                                        }
                                                                    </MenuItem>
                                                                )
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                    {/* Conditionally render extra margin fields for trailingstop */}
                                                    {detail.strategy &&
                                                        detail.strategy.name ===
                                                            'trailingstop' && (
                                                            <>
                                                                <TextField
                                                                    label="Ceiling Margin"
                                                                    type="number"
                                                                    value={
                                                                        detail
                                                                            .strategy
                                                                            .trailing_stop_strategy
                                                                            .ceiling_margin ||
                                                                        ''
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        handleTrailingMarginChange(
                                                                            row
                                                                                .original
                                                                                .uuid,
                                                                            index,
                                                                            'ceiling_margin',
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    onFocus={() =>
                                                                        setFocusedField(
                                                                            {
                                                                                rowId: row
                                                                                    .original
                                                                                    .uuid,
                                                                                detailIndex:
                                                                                    index,
                                                                                fieldType:
                                                                                    'margin'
                                                                            }
                                                                        )
                                                                    }
                                                                    onBlur={() =>
                                                                        setFocusedField(
                                                                            null
                                                                        )
                                                                    }
                                                                    size="small"
                                                                    InputLabelProps={{
                                                                        sx: {
                                                                            fontSize:
                                                                                '14px',
                                                                            fontWeight:
                                                                                'bold',
                                                                            color: 'gray'
                                                                        }
                                                                    }}
                                                                    InputProps={{
                                                                        sx: {
                                                                            fontSize:
                                                                                '14px',
                                                                            fontWeight:
                                                                                'bold',
                                                                            color: 'gray'
                                                                        }
                                                                    }}
                                                                />
                                                                <TextField
                                                                    label="Floor Margin"
                                                                    type="number"
                                                                    value={
                                                                        detail
                                                                            .strategy
                                                                            .trailing_stop_strategy
                                                                            .floor_margin ||
                                                                        ''
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        handleTrailingMarginChange(
                                                                            row
                                                                                .original
                                                                                .uuid,
                                                                            index,
                                                                            'floor_margin',
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    onFocus={() =>
                                                                        setFocusedField(
                                                                            {
                                                                                rowId: row
                                                                                    .original
                                                                                    .uuid,
                                                                                detailIndex:
                                                                                    index,
                                                                                fieldType:
                                                                                    'margin'
                                                                            }
                                                                        )
                                                                    }
                                                                    onBlur={() =>
                                                                        setFocusedField(
                                                                            null
                                                                        )
                                                                    }
                                                                    size="small"
                                                                    InputLabelProps={{
                                                                        sx: {
                                                                            fontSize:
                                                                                '14px',
                                                                            fontWeight:
                                                                                'bold',
                                                                            color: 'gray'
                                                                        }
                                                                    }}
                                                                    InputProps={{
                                                                        sx: {
                                                                            fontSize:
                                                                                '14px',
                                                                            fontWeight:
                                                                                'bold',
                                                                            color: 'gray'
                                                                        }
                                                                    }}
                                                                />
                                                                {/* Display calculated prices only when this specific detail's margin fields are focused */}
                                                                {focusedField &&
                                                                    focusedField.rowId ===
                                                                        row
                                                                            .original
                                                                            .uuid &&
                                                                    focusedField.detailIndex ===
                                                                        index &&
                                                                    focusedField.fieldType ===
                                                                        'margin' && (
                                                                        <Box
                                                                            sx={{
                                                                                display:
                                                                                    'flex',
                                                                                flexDirection:
                                                                                    'column',
                                                                                gap: 1,
                                                                                minWidth: 220,
                                                                                p: 2,
                                                                                border: '1px solid #e0e0e0',
                                                                                borderRadius: 1,
                                                                                backgroundColor:
                                                                                    '#f8f9fa'
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    fontWeight:
                                                                                        'bold',
                                                                                    color: '#1976d2',
                                                                                    mb: 1
                                                                                }}
                                                                            >
                                                                                📊
                                                                                Trading
                                                                                Parameters
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                    fontWeight:
                                                                                        'bold',
                                                                                    color: '#424242'
                                                                                }}
                                                                            >
                                                                                Base
                                                                                Price:{' '}
                                                                                {detail
                                                                                    .strategy
                                                                                    ?.trailing_stop_strategy
                                                                                    ?.price
                                                                                    ? `€${Number(
                                                                                          detail
                                                                                              .strategy
                                                                                              .trailing_stop_strategy
                                                                                              .price
                                                                                      ).toFixed(
                                                                                          2
                                                                                      )}`
                                                                                    : 'N/A'}
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                    color: '#666'
                                                                                }}
                                                                            >
                                                                                Ceiling
                                                                                Margin:{' '}
                                                                                {detail
                                                                                    .strategy
                                                                                    ?.trailing_stop_strategy
                                                                                    ?.ceiling_margin ||
                                                                                    'Not set'}
                                                                            </Typography>
                                                                            {detail
                                                                                .strategy
                                                                                ?.trailing_stop_strategy
                                                                                ?.ceiling_margin && (
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        color: '#2e7d32',
                                                                                        fontWeight:
                                                                                            'bold',
                                                                                        backgroundColor:
                                                                                            '#e8f5e8',
                                                                                        p: 0.5,
                                                                                        borderRadius: 0.5
                                                                                    }}
                                                                                >
                                                                                    🔴
                                                                                    Ceiling
                                                                                    Price:
                                                                                    €
                                                                                    {(
                                                                                        Number(
                                                                                            detail
                                                                                                .strategy
                                                                                                .trailing_stop_strategy
                                                                                                .price ||
                                                                                                0
                                                                                        ) +
                                                                                        Number(
                                                                                            detail
                                                                                                .strategy
                                                                                                .trailing_stop_strategy
                                                                                                .ceiling_margin
                                                                                        )
                                                                                    ).toFixed(
                                                                                        2
                                                                                    )}
                                                                                </Typography>
                                                                            )}
                                                                            <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                    color: '#666'
                                                                                }}
                                                                            >
                                                                                Floor
                                                                                Margin:{' '}
                                                                                {detail
                                                                                    .strategy
                                                                                    ?.trailing_stop_strategy
                                                                                    ?.floor_margin ||
                                                                                    'Not set'}
                                                                            </Typography>
                                                                            {detail
                                                                                .strategy
                                                                                ?.trailing_stop_strategy
                                                                                ?.floor_margin && (
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        color: '#d32f2f',
                                                                                        fontWeight:
                                                                                            'bold',
                                                                                        backgroundColor:
                                                                                            '#ffebee',
                                                                                        p: 0.5,
                                                                                        borderRadius: 0.5
                                                                                    }}
                                                                                >
                                                                                    🟢
                                                                                    Floor
                                                                                    Price:
                                                                                    €
                                                                                    {(
                                                                                        Number(
                                                                                            detail
                                                                                                .strategy
                                                                                                .trailing_stop_strategy
                                                                                                .price ||
                                                                                                0
                                                                                        ) +
                                                                                        Number(
                                                                                            detail
                                                                                                .strategy
                                                                                                .trailing_stop_strategy
                                                                                                .floor_margin
                                                                                        )
                                                                                    ).toFixed(
                                                                                        2
                                                                                    )}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    )}
                                                            </>
                                                        )}
                                                    {canRemove ? (
                                                        <IconButton
                                                            onClick={() =>
                                                                handleRemoveDetail(
                                                                    row.original
                                                                        .uuid,
                                                                    index
                                                                )
                                                            }
                                                        >
                                                            <Remove />
                                                        </IconButton>
                                                    ) : (
                                                        <Tooltip title="Cannot remove after purchases have been made">
                                                            <span>
                                                                <IconButton
                                                                    disabled
                                                                >
                                                                    <Remove />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            );
                                        }
                                    );
                                })()}
                                {(!expandedRows[row.original.uuid] ||
                                    expandedRows[row.original.uuid].length ===
                                        0) && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() =>
                                                handleEnsureEditingAndAddDetail(
                                                    row
                                                )
                                            }
                                            sx={{
                                                fontSize: '13px',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            Add Detail
                                        </Button>
                                    </Box>
                                )}
                                {expandedRows[row.original.uuid]?.length >
                                    0 && (
                                    <Stack direction="row" spacing={2}>
                                        <Tooltip
                                            title={
                                                getValidationMessage(
                                                    row.original.uuid
                                                ) || ''
                                            }
                                            arrow
                                            placement="top"
                                        >
                                            <span>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<Save />}
                                                    onClick={() =>
                                                        handleSaveRow(
                                                            row.original.uuid
                                                        )
                                                    }
                                                    disabled={isSaveDisabled(
                                                        row.original.uuid
                                                    )}
                                                >
                                                    Save
                                                </Button>
                                            </span>
                                        </Tooltip>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() =>
                                                handleCancelRowEdit(
                                                    row.original.uuid
                                                )
                                            }
                                        >
                                            Cancel Edit
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={() =>
                                                handleEnsureEditingAndAddDetail(
                                                    row
                                                )
                                            }
                                            sx={{
                                                fontSize: '13px',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            ADD MORE DETAILS
                                        </Button>
                                    </Stack>
                                )}
                            </Stack>
                        )}
                    </Box>
                )}
                muiTableHeadCellProps={{
                    align: 'center',
                    sx: {
                        backgroundColor: '#CBF0E6',
                        color: '#245372',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        borderTop: '2px solid #3DC3C1',
                        borderBottom: '2px solid #3DC3C1'
                    }
                }}
                muiTableBodyRowProps={({ row }) => {
                    return {
                        sx: {
                            backgroundColor:
                                row.index % 2 === 0 ? '#ffffff' : '#f5f5f5'
                            // '&:hover': {
                            //     backgroundColor:
                            //         row.index % 2 === 0 ? '#f0f8ff' : '#e6f2ff'
                            // }
                        }
                    };
                }}
                muiTableBodyCellProps={({ cell }) => ({
                    sx: {
                        // fontSize: "10px",
                        // Add border to ALL cells instead of just first and last
                        borderRight: '1px solid #E0E0E0'
                    },
                    align: 'center'
                })}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert
                    severity="success"
                    onClose={() => setSnackbarOpen(false)}
                >
                    Changes saved successfully!
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!errorMessage}
                autoHideDuration={3000}
                onClose={() => setErrorMessage('')}
            >
                <Alert severity="error" onClose={() => setErrorMessage('')}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default HedgingTable;
